import React from 'react';
import { translations } from 'locales/translations';
import { useTranslation } from 'react-i18next';
import { PopupImportExportDialog } from './Components/Dialog';
import { PopupImportExportStep1 } from './Components/step1';
import { PopupImportExportProgress } from './Components/step2_4';
import { PopupImportExportStep3 } from './Components/step3';
import { PopupImportExportStep5 } from './Components/step5';
import { ImprotData } from './Data';
import { ScreensId } from 'enums/ConfigurableTypes';
import { GenericImport } from './Actions/Import';
import { ImportAdditionalData, ImportResult } from './Actions/Types';
import fileDownload from 'js-file-download';
import { ExportImportActions } from './Actions';
import { Entity } from 'types/common';
import { useLayoutSlice } from 'app/Layout/FrontendLayout/slice';
import { useDispatch } from 'react-redux';

/**
 *
 * PopupExportImport
 *
 */
export interface PopupImportExportProps {
  title: string;
  show: boolean;
  screenId: ScreensId;
  budgetType?: number;
  iad?: ImportAdditionalData;
  close: () => void;
}
export function PopupImportExport({
  title,
  show,
  screenId,
  budgetType,
  iad,
  close,
}: PopupImportExportProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { actions } = useLayoutSlice();
  const [step, setStep] = React.useState<number>(0);
  const [rows, setRows] = React.useState<number>(0);
  const [columns, setColumns] = React.useState<number>(0);
  const [filetype, setFiletype] = React.useState<Entity<string> | null>({
    Id: '.xlsx',
    Name: '.xlsx',
  });
  const [importResult, setImportResult] = React.useState<ImportResult>(
    ImportResult.default,
  );
  const [resultData, setResultData] = React.useState<any | undefined>();
  const [resultFileName, setResultFileName] = React.useState<
    string | undefined
  >();
  const [newRows, setNewRows] = React.useState<number>(0);
  const [existingRows, setExistingRows] = React.useState<number>(0);
  const [errors, setErrors] = React.useState<number>(0);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  React.useEffect(() => {
    ImprotData.ClearImages();
    ImprotData.ScreenId = screenId;
    ImprotData.ImportAdditionalData =
      iad !== undefined ? iad : ImportAdditionalData.none;
  }, [iad, screenId]);
  const cancel = React.useCallback(() => {
    setRows(0);
    setColumns(0);
    setResultData(undefined);
    setStep(0);
    ImprotData.ClearImages();
    close();
  }, [close]);
  const cancelFileUpload = React.useCallback(() => {
    cancel();
  }, [cancel]);
  const cancelImport = React.useCallback(() => {
    cancel();
  }, [cancel]);
  const back = React.useCallback(() => {
    if (step > 1) {
      setStep(step - 2);
    }
  }, [step]);
  /*const delay = delayInms => {
    return new Promise(resolve => setTimeout(resolve, delayInms));
  };*/
  const handleFileTypeChange = React.useCallback(
    (value: Entity<string> | null) => {
      setFiletype(value);
    },
    [],
  );
  const ImportTenplateHandler = React.useCallback(async () => {
    const res = await ExportImportActions.ImportTemplate(
      screenId,
      filetype !== null ? filetype.Name : '.xlsx',
      iad,
    );
    const cd = res.headers['content-disposition'];
    const filename =
      cd !== undefined
        ? cd.split('filename=')[1]
        : 'template' + (filetype !== null ? filetype.Name : '');
    fileDownload(res.data, filename);
  }, [filetype, iad, screenId]);
  const next = React.useCallback(async () => {
    switch (step) {
      case 0:
        setStep(1);
        const res = await ImprotData.LoadExcelfile();
        if (res !== undefined) {
          setRows(ImprotData.RowCount !== undefined ? ImprotData.RowCount : 0);
          const cc = await ImprotData.LoadTemplateColumns();
          if (cc !== undefined) {
            ImprotData.SetTemplateColumns(cc);
          }
          //await delay(5000);
          setStep(2);
        } else {
          setStep(0);
        }
        break;
      case 1:
        break;
      case 2:
        setColumns(ImprotData.ColumnCount());
        setStep(3);
        const importFileResult = await GenericImport(ImprotData.File, {
          ScreenId: screenId,
          Columns: ImprotData.Header !== undefined ? ImprotData.Header : [],
          BudgetType: budgetType ?? 1,
          AddOnly: ImprotData.AddOnly ?? false,
          Iad: iad ?? ImportAdditionalData.none,
          ImportAssetImageFiles: ImprotData.ImportAssetImageFiles,
          AutoGenerateAssetNamesBasedOn:
            ImprotData.AutoGenerateAssetNamesBasedOn?.Id,
          UpdateQRCode: ImprotData.UpdateQRCode,
        });
        //await delay(5000);
        setResultData(
          importFileResult !== undefined
            ? importFileResult.resultData
            : undefined,
        );
        setResultFileName(
          importFileResult !== undefined
            ? importFileResult.resultFileName
            : undefined,
        );
        setImportResult(
          importFileResult !== undefined
            ? importFileResult.importResult
            : ImportResult.default,
        );
        setErrorMessage(
          importFileResult !== undefined
            ? importFileResult.errorMessage
            : undefined,
        );
        setNewRows(
          importFileResult !== undefined
            ? importFileResult.NewRecordsImported
            : 0,
        );
        setExistingRows(
          importFileResult !== undefined
            ? importFileResult.ExistingRecordsUpdated
            : 0,
        );
        setErrors(
          importFileResult !== undefined ? importFileResult.ImportErrors : 0,
        );
        dispatch(actions.setRefreshTable(true));
        setStep(4);
        break;
      case 3:
        break;
      case 4:
        break;
      default:
    }
  }, [actions, budgetType, dispatch, iad, screenId, step]);
  const handleDownloadImportErrorReport = React.useCallback(() => {
    if (
      resultData !== undefined &&
      resultFileName !== undefined &&
      resultFileName !== ''
    ) {
      fileDownload(resultData, resultFileName);
      setResultData(undefined);
    }
  }, [resultData, resultFileName]);
  const steps = React.useMemo((): React.ReactNode[] => {
    return [
      <PopupImportExportStep1
        title={title}
        filetype={filetype}
        nextName={t(translations.Next)}
        cancelName={t(translations.Cancel)}
        ImportTenplateHandler={ImportTenplateHandler}
        handleFileTypeChange={handleFileTypeChange}
        next={next}
        cancel={cancel}
      />,
      <PopupImportExportProgress
        title={t(translations.UploadingFile)}
        buttonName={t(translations.CancelFileUpload)}
        info={rows.toString() + ' ' + t(translations.RowsFound)}
        onClick={cancelFileUpload}
      />,
      <PopupImportExportStep3
        title={title}
        backName={t(translations.Back)}
        nextName={t(translations.Next)}
        cancelName={t(translations.Cancel)}
        rows={rows}
        back={back}
        next={next}
        cancel={cancel}
      />,
      <PopupImportExportProgress
        title={t(translations.ImportingData)}
        buttonName={t(translations.CancelImport)}
        info={
          rows.toString() +
          ' ' +
          t(translations.ImportRowsFor) +
          ' ' +
          columns.toString() +
          ' ' +
          t(translations.ImportColumns)
        }
        onClick={cancelImport}
      />,
      <PopupImportExportStep5
        state={importResult}
        newRows={newRows}
        existingRows={existingRows}
        errors={errors}
        errorMessage={errorMessage}
        handleDownloadImportErrorReport={handleDownloadImportErrorReport}
        onClick={cancelFileUpload}
      />,
    ];
  }, [
    ImportTenplateHandler,
    back,
    cancel,
    cancelFileUpload,
    cancelImport,
    columns,
    errorMessage,
    errors,
    existingRows,
    filetype,
    handleDownloadImportErrorReport,
    handleFileTypeChange,
    importResult,
    newRows,
    next,
    rows,
    t,
    title,
  ]);
  return (
    <>
      <PopupImportExportDialog open={show} content={steps[step]} />
    </>
  );
}
